import {
  ApiChartAttribute,
  ApiDimension,
  ApiDimensionCategory,
  ApiOperator,
} from "@incendium/api";
import { Add } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Stack,
  Button,
  GridSize,
} from "@mui/material";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  AccordianChartBuilderSidebarBlock,
  ChartAttributesSelected,
} from "../";
import { CallbackOrVal, TFilterableDimensions } from "Interfaces";
import produce from "immer";
import { usePrevious, useUpdateEffect } from "react-use";
import { formatEnumVal } from "Helpers/enumToText";
import { useFilterableDimensions } from "features/analytics";
import Loading from "Components/Loading/Loading";
import { TypographyHelp } from "Components/UI/TypographyHelp";
import { filterObjectByKeys } from "Helpers/objects";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { uniqueArray } from "Helpers/arrays";
import { ColsCol, ColsLayout } from "Components/ColsLayout/ColsLayout";
import { useDimensionLabels } from "features/analytics/hooks/useDimensionLabels";
import {
  dimensionCategoryTitle,
  orderGroupedDimensionLabels,
} from "features/analytics/utils/utils";

const defaultColWidth: GridSize[] = [4, 3, 5];
const expandedColWidths: GridSize[][] = [
  [8, 2, 2],
  [3, 6, 3],
  [2, 2, 8],
];

const ChartBuilderAttributesRow = ({
  options,
  passedAtribute,
  setPassedAtribute,
  attributes,
  setAttributes,
  getDimensionValues,
}: {
  options?: TFilterableDimensions;
  passedAtribute?: ApiChartAttribute;
  setPassedAtribute?: (att: ApiChartAttribute | undefined) => void;
  attributes: ApiChartAttribute[];
  setAttributes: (att: ApiChartAttribute[]) => void;
  getDimensionValues: (
    dimension: ApiDimension,
  ) => Promise<string[] | undefined>;
}) => {
  const [key, setKey] = useState<ApiDimension>();
  const prevKey = usePrevious(key);
  const [values, setValues] = useState<string[]>([]);
  const [operator, setOperator] = useState(ApiOperator.EQUAL);
  const dimensionToName = useDimensionName();
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [colWidths, setColWidths] = useState<GridSize[]>(defaultColWidth);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(
    null,
  );
  const { dimensionLabels } = useDimensionLabels();

  useEffect(() => {
    if (passedAtribute) {
      setKey(passedAtribute.key as ApiDimension);
      setOperator(passedAtribute.operator as ApiOperator);
      setValues([passedAtribute.value as string]);
    }
  }, [passedAtribute]);

  useEffect(() => {
    if (selectedColumnIndex !== null) {
      setColWidths(expandedColWidths[selectedColumnIndex]);
    } else {
      setColWidths(defaultColWidth);
    }
  }, [selectedColumnIndex]);

  const handleOpen = useCallback((index: number) => {
    setOpenIndex(null);
    setSelectedColumnIndex(null);
    setSelectedColumnIndex(index);
    setTimeout(() => {
      setOpenIndex(index);
    }, 400);
  }, []);

  const handleClose = useCallback(() => {
    setOpenIndex(null);
    setSelectedColumnIndex(null);
  }, []);

  const optionLabels = useMemo(() => {
    const arr = Object.keys(options || {}).map((s) => {
      const f = dimensionLabels.find((d) => d.dimension === s);
      return {
        category:
          f && f.category
            ? f.category
            : ApiDimensionCategory.DIMENSION_CATEGORY_UNKNOWN,
        label: s,
      };
    });

    const sorted = orderGroupedDimensionLabels(arr);
    return sorted.map((s) => ({
      ...s,
      category: dimensionCategoryTitle(
        s.category || ApiDimensionCategory.DIMENSION_CATEGORY_UNKNOWN,
      ) as ApiDimensionCategory,
    }));
  }, [options, dimensionLabels]);

  const selectedOption = useMemo(
    () => optionLabels.find((o) => o.label === key),
    [optionLabels, key],
  );

  useEffect(() => {
    const fetchAvailableOptions = async () => {
      if (!key) {
        setAvailableOptions([]);
        return;
      }

      setLoadingOptions(true);
      try {
        const dimensionValues = await getDimensionValues(key);
        const selected = attributes
          .filter(
            (f) => f.key === key && (!f.operator || f.operator === operator),
          )
          .map((v) => v.value);

        const filteredOptions = (dimensionValues || []).filter(
          (v) => !selected.includes(v),
        );
        setAvailableOptions(uniqueArray(filteredOptions));
      } catch (error) {
        console.error("Error fetching dimension values:", error);
        setAvailableOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchAvailableOptions();
  }, [key, operator, attributes, getDimensionValues]);

  useUpdateEffect(() => {
    if (!passedAtribute && key !== prevKey) {
      setValues([]);
    }
  }, [key]);

  const addAtt = useCallback(() => {
    setAttributes(
      produce(attributes, (draft) => {
        values.forEach((value) => {
          const idx = draft.findIndex(
            (d) =>
              d.key === key && d.operator === operator && d.value === value,
          );
          if (idx < 0) {
            draft.push({
              key,
              operator,
              value,
            });
          }
        });
      }),
    );
    setKey(undefined);
    setOperator(ApiOperator.EQUAL);
    setValues([]);
    setPassedAtribute && setPassedAtribute(undefined);
  }, [values, operator, key, setAttributes, attributes, setPassedAtribute]);

  return (
    <Stack spacing={2}>
      <ColsLayout spacing={2} sx={{ marginLeft: "-16px" }}>
        <ColsCol width={colWidths[0]} noborder>
          <Autocomplete
            id="att-dim"
            size="small"
            key={key}
            onChange={(e, v) => {
              setKey(v.label as ApiDimension);
            }}
            value={selectedOption}
            disableClearable
            groupBy={(o) => o.category || ""}
            fullWidth
            open={openIndex === 0}
            onMouseOver={() => openIndex === null && setSelectedColumnIndex(0)}
            onMouseOut={() =>
              openIndex === null && setSelectedColumnIndex(null)
            }
            onOpen={() => {
              handleOpen(0);
            }}
            onClose={handleClose}
            options={optionLabels || []}
            getOptionLabel={(o) => dimensionToName(o.label as ApiDimension)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Dimension"
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiFormLabel-root": {
                    maxWidth: "calc(100% - 34px)",
                  },
                }}
              />
            )}
          />
        </ColsCol>
        <ColsCol width={colWidths[1]} noborder>
          <TextField
            select
            label="Operator"
            size="small"
            fullWidth
            onMouseOver={() => openIndex === null && setSelectedColumnIndex(1)}
            onMouseOut={() =>
              openIndex === null && setSelectedColumnIndex(null)
            }
            value={operator}
            onChange={(e) => {
              setOperator(e.target.value as ApiOperator);
            }}
            SelectProps={{
              open: openIndex === 1,
              onOpen: () => {
                handleOpen(1);
              },
              onClose: handleClose,
            }}
          >
            {[
              ApiOperator.EQUAL,
              ApiOperator.NOT_EQUAL,
              ApiOperator.CONTAINS,
              ApiOperator.NOT_CONTAINS,
            ].map((o) => (
              <MenuItem key={o} value={o}>
                {formatEnumVal(o)}
              </MenuItem>
            ))}
          </TextField>
        </ColsCol>
        <ColsCol width={colWidths[2]} noborder>
          <Box
            onMouseOver={() => openIndex === null && setSelectedColumnIndex(2)}
            onMouseOut={() =>
              openIndex === null && setSelectedColumnIndex(null)
            }
          >
            {[ApiOperator.EQUAL, ApiOperator.NOT_EQUAL].includes(operator) ? (
              <Autocomplete
                id="att-value"
                multiple
                onChange={(e, value) => {
                  setValues(value);
                }}
                value={values}
                filterSelectedOptions
                disableClearable
                open={openIndex === 2}
                onOpen={() => {
                  handleOpen(2);
                }}
                onClose={handleClose}
                fullWidth
                options={availableOptions || []}
                loading={loadingOptions}
                ChipProps={{
                  size: "small",
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Value"
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            ) : (
              <TextField
                label="Value contains "
                size="small"
                fullWidth
                value={values[0] || ""}
                onChange={(e) => {
                  setValues([e.target.value]);
                }}
              />
            )}
          </Box>
        </ColsCol>
      </ColsLayout>
      <Box pl={2}>
        <Button
          color="secondary"
          size="extraSmall"
          endIcon={<Add />}
          onClick={addAtt}
          disabled={!key || values.length === 0}
        >
          Add Filter
        </Button>
      </Box>
    </Stack>
  );
};

interface IChartBuilderAttributesProps {
  noPadding?: boolean;
  title?: string;
  subTitle?: string;
  attributes: ApiChartAttribute[];
  setAttributes: (val: CallbackOrVal<ApiChartAttribute[]>) => void;
  dimensions?: ApiDimension[];
  defaultClosed?: boolean;
}

function ChartBuilderAttributes({
  title,
  subTitle,
  attributes,
  setAttributes,
  dimensions,
  defaultClosed,
  noPadding,
}: IChartBuilderAttributesProps) {
  const dimensionToName = useDimensionName();
  const { filterableDimensions, loading, getDimensionValues } =
    useFilterableDimensions();
  const [selectedAttribute, setSelectedAttribute] = useState<
    ApiChartAttribute | undefined
  >();

  const onPillClick = useCallback((attribute) => {
    setSelectedAttribute(attribute);
  }, []);

  const selectableFilters = useMemo(() => {
    if (
      !dimensions ||
      dimensions.length === 0 ||
      (dimensions.length === 1 &&
        dimensions[0] === ApiDimension.DIMENSION_NOT_SET)
    ) {
      return filterableDimensions;
    }
    return filterObjectByKeys(filterableDimensions, dimensions);
  }, [filterableDimensions, dimensions]);

  return (
    <AccordianChartBuilderSidebarBlock
      defaultClosed={defaultClosed}
      title={title || "Chart Filters"}
      noPadding={noPadding}
      subTitle={
        <>
          <TypographyHelp
            iconSize="extraSmall"
            variant="body2"
            text={
              subTitle ||
              "Chart filters are permanent. Click the question mark to find out more"
            }
            tooltip={
              <>
                <Typography variant="body2" mb={1}>
                  Chart Filters are permanent filters, for example if you added
                  the "{dimensionToName(ApiDimension.DIMENSION_DEVICE)} =
                  mobile" attribute, this chart will exclusively display data
                  related to mobile devices.
                </Typography>
                <Typography variant="body2" mb={1}>
                  When selecting multiple attributes within the same dimension,
                  they will be treated as an "OR" condition. For instance, if
                  you choose {dimensionToName(ApiDimension.DIMENSION_CHANNEL)} =
                  direct and {dimensionToName(ApiDimension.DIMENSION_CHANNEL)} =
                  organic, the chart will display data where the{" "}
                  {dimensionToName(ApiDimension.DIMENSION_CHANNEL)} is either
                  direct or organic.
                </Typography>
                <Typography variant="body2">
                  To apply filters, select your desired dimension and value, and
                  click the "Add" button to add the filter.
                </Typography>
              </>
            }
          />
        </>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          <Box mb={2}>
            <ChartBuilderAttributesRow
              options={selectableFilters}
              attributes={attributes}
              setAttributes={setAttributes}
              passedAtribute={selectedAttribute}
              setPassedAtribute={setSelectedAttribute}
              getDimensionValues={getDimensionValues}
            />
          </Box>
          {attributes.length > 0 && <Divider />}
          <ChartAttributesSelected
            mt={2}
            attributes={attributes}
            setAttributes={setAttributes}
            onClick={onPillClick}
          />
        </>
      )}
    </AccordianChartBuilderSidebarBlock>
  );
}

export default ChartBuilderAttributes;
